/* eslint-disable consistent-return */
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import Check from '@material-ui/icons/Check';
import ArrowDown from '@material-ui/icons/ArrowDownward';
import ArrowUp from '@material-ui/icons/ArrowUpward';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import withStyles from '@material-ui/core/styles/withStyles';
import FilterStyles from 'assets/jss/AdsFilter';
import Card from 'components/UI/Card/Card';
import CardBody from 'components/UI/Card/CardBody';
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import CustomInput from 'components/UI/CustomInput/CustomInput';
import SectionDivider from 'components/common/SectionDivider/SectionDivider';
import Datepicker from 'components/common/DatePicker';
import { reviewStatusList } from 'components/Review/helpers';

const dateRangeGroups = [
  ['createdOn', 'created'],
  ['updatedOn', 'updated'],
  ['publishedOn', 'published'],
  ['reviewedOn', 'reviewed'],
  ['lastReportedOn', 'lastReported'],
];

const reviewFilterStyles = {
  ...FilterStyles,
  reviewExpandFilters: {
    maxHeight: 3000,
    '@media (min-width: 767px)': {
      maxHeight: 3000,
    },
  },
};

class ReviewFilter extends React.Component {
  constructor(props) {
    super(props);
    const { appliedFilter } = props;
    this.state = {
      localFilter: appliedFilter || {},
      selectedStatus: (appliedFilter && appliedFilter.status) || [],
      expandFiltersAnimation: false,
    };
  }

  handleChangeFilter = changedFilter => {
    const { localFilter } = this.state;
    this.setState({ localFilter: { ...localFilter, ...changedFilter } });
  };

  handleStatusCheck = status => {
    const { selectedStatus } = this.state;
    const nextStatus =
      selectedStatus.indexOf(status) !== -1
        ? selectedStatus.filter(item => item !== status)
        : [...selectedStatus, status];
    this.setState(
      {
        selectedStatus: nextStatus,
      },
      () =>
        this.handleChangeFilter({
          status: nextStatus.length ? nextStatus : undefined,
        }),
    );
  };

  handleFilter = () => {
    const { filter } = this.props;
    const { localFilter } = this.state;
    filter({ ...localFilter });
  };

  expandAnimation = () => {
    const { expandFiltersAnimation } = this.state;
    const { showFilters } = this.props;
    this.setState({ expandFiltersAnimation: !expandFiltersAnimation }, () =>
      setTimeout(showFilters, 150),
    );
  };

  renderInputFilter = (field, type = 'text') => {
    const { classes, t } = this.props;
    const { localFilter } = this.state;
    return (
      <CustomInput
        id={`review-filter-${field}`}
        formControlProps={{
          className: `${classes.top} ${classes.search}`,
        }}
        inputProps={{
          placeholder: t(`reviews.filters.${field}`),
          onChange: e => {
            const { value } = e.target;
            this.handleChangeFilter({
              [field]: value !== '' ? value : undefined,
            });
          },
          value: localFilter[field] !== undefined ? localFilter[field] : '',
          type,
        }}
      />
    );
  };

  renderDateRange = ([field, translationKey]) => {
    const { classes, t } = this.props;
    const { localFilter } = this.state;
    const gteField = `${field}Gte`;
    const lteField = `${field}Lte`;
    return (
      <GridItem xs={12} sm={12} md={6} key={field}>
        <h6 className={classes.title}>
          {t(`reviews.filters.dates.${translationKey}`)}
        </h6>
        <GridContainer>
          <GridItem xs={12} sm={12} md={6}>
            <Datepicker
              title={t('ad.filters.date.from')}
              defaultValue={t('ad.filters.date.from_placeholder')}
              onChange={date => {
                if (localFilter[lteField] && localFilter[lteField] < date) {
                  return;
                }
                this.handleChangeFilter({
                  [gteField]: date
                    ? moment(date)
                        .startOf('day')
                        .format()
                    : null,
                });
              }}
              value={
                localFilter[gteField] !== undefined &&
                localFilter[gteField] !== null
                  ? moment(localFilter[gteField])
                  : ''
              }
              toToday
              validGte={localFilter[lteField]}
            />
          </GridItem>
          <GridItem xs={12} sm={12} md={6}>
            <Datepicker
              title={t('ad.filters.date.to')}
              defaultValue={t('ad.filters.date.to_placeholder')}
              onChange={date => {
                if (localFilter[gteField] && localFilter[gteField] > date) {
                  return;
                }
                this.handleChangeFilter({
                  [lteField]: date
                    ? moment(date)
                        .endOf('day')
                        .format()
                    : null,
                });
              }}
              value={
                localFilter[lteField] !== undefined &&
                localFilter[lteField] !== null
                  ? moment(localFilter[lteField])
                  : ''
              }
              toToday
              validLte={localFilter[gteField]}
            />
          </GridItem>
        </GridContainer>
      </GridItem>
    );
  };

  renderCollapsedFilters() {
    const { expandFiltersAnimation } = this.state;
    const { classes, t } = this.props;
    return (
      <div
        className={`${classes.animationFilter}
      ${
        expandFiltersAnimation
          ? classes.expandCollapsedFilters
          : classes.collapseCollapsedFilters
      }`}
      >
        <GridContainer>
          <GridItem xs={12} style={{ textAlign: 'center' }}>
            <Button
              color="primary"
              simple
              className={classes.showFilters}
              onClick={this.expandAnimation}
              id="showFilters"
              key="showFilters"
              fullWidth
            >
              <ArrowDown />
              {t('ad.filters.expand')}
            </Button>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  renderFilters() {
    const { localFilter, selectedStatus, expandFiltersAnimation } = this.state;
    const { classes, t, restartFilters } = this.props;
    return (
      <div
        className={`${classes.animationFilter}
      ${
        expandFiltersAnimation
          ? `${classes.expandFilters} ${classes.reviewExpandFilters}`
          : classes.collapseFilters
      }`}
      >
        <GridContainer>
          <GridItem xs={12} md={6}>
            <SectionDivider label={t('reviews.filters.sections.review')}>
              <GridContainer>
                <GridItem xs={12} md={4}>
                  {this.renderInputFilter('reviewer')}
                </GridItem>
                <GridItem xs={12} md={4}>
                  {this.renderInputFilter('reviewee')}
                </GridItem>
                <GridItem xs={12} md={4}>
                  {this.renderInputFilter('moderator')}
                </GridItem>
                <GridItem xs={12} md={4}>
                  {this.renderInputFilter('rate', 'number')}
                </GridItem>
                <GridItem xs={12} md={4}>
                  {this.renderInputFilter('reportCountGte', 'number')}
                </GridItem>
                <GridItem xs={12} md={4}>
                  {this.renderInputFilter('reportCountLte', 'number')}
                </GridItem>
                <GridItem xs={12}>
                  <span>{t('reviews.filters.hasReports')}</span>
                  <div className={classes.buttonGroup}>
                    <Button
                      color="info"
                      round
                      className={classes.firstButton}
                      simple={
                        localFilter.hasReports === undefined ||
                        localFilter.hasReports === true
                      }
                      onClick={() =>
                        this.handleChangeFilter({ hasReports: false })
                      }
                      id="reviews-has-reports-no"
                    >
                      {t('reviews.filters.no')}
                    </Button>
                    <Button
                      color="info"
                      round
                      className={classes.middleButton}
                      simple={localFilter.hasReports !== undefined}
                      onClick={() =>
                        this.handleChangeFilter({ hasReports: undefined })
                      }
                      id="reviews-has-reports-all"
                    >
                      {t('reviews.filters.all')}
                    </Button>
                    <Button
                      color="info"
                      round
                      className={classes.lastButton}
                      simple={
                        localFilter.hasReports === undefined ||
                        localFilter.hasReports === false
                      }
                      onClick={() =>
                        this.handleChangeFilter({ hasReports: true })
                      }
                      id="reviews-has-reports-yes"
                    >
                      {t('reviews.filters.yes')}
                    </Button>
                  </div>
                </GridItem>
              </GridContainer>
            </SectionDivider>
          </GridItem>
          <GridItem xs={12} md={6}>
            <SectionDivider label={t('reviews.filters.sections.status')}>
              <GridContainer>
                {reviewStatusList.map(status => (
                  <GridItem xs={12} sm={6} key={status}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          className={classes.checked}
                          onClick={() => this.handleStatusCheck(status)}
                          checked={selectedStatus.indexOf(status) !== -1}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                        />
                      }
                      id={`review-status-${status}`}
                      label={t(`reviews.status.${status}`)}
                      style={{ width: '100%' }}
                    />
                  </GridItem>
                ))}
              </GridContainer>
            </SectionDivider>
          </GridItem>
          <GridItem xs={12}>
            <SectionDivider label={t('reviews.filters.sections.dates')}>
              <GridContainer>
                {dateRangeGroups.map(group => this.renderDateRange(group))}
              </GridContainer>
            </SectionDivider>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={6}>
            <Button
              color="primary"
              simple
              className={classes.buttons}
              onClick={this.expandAnimation}
              id="hideFilters"
              key="hideFilters"
            >
              <ArrowUp />
              {t('ad.filters.shrink')}
            </Button>
          </GridItem>
          <GridItem xs={12} sm={12} md={6}>
            <GridContainer justify="flex-end">
              <GridItem>
                <Button
                  color="danger"
                  simple
                  className={classes.buttons}
                  onClick={() => {
                    this.setState({
                      selectedStatus: [],
                      localFilter: {},
                    });
                    restartFilters();
                  }}
                  id="restartFilters"
                  key="restartFilters"
                >
                  {t('ad.filters.clean')}
                </Button>
                <Button
                  color="primary"
                  className={classes.buttons}
                  onClick={() => {
                    this.expandAnimation();
                    this.handleFilter();
                  }}
                  id="filterReviews"
                  key="filterReviews"
                >
                  {t('ad.filters.filter')}
                </Button>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  render() {
    const { showCollapseFilters } = this.props;
    return (
      <Card>
        <CardBody>
          {!showCollapseFilters
            ? this.renderFilters()
            : this.renderCollapsedFilters()}
        </CardBody>
      </Card>
    );
  }
}

ReviewFilter.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  showCollapseFilters: PropTypes.bool.isRequired,
  showFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(withStyles(reviewFilterStyles)(ReviewFilter));
